import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Drawer,
  EmptyState,
  EmployeeAvatar,
  Field,
  IconBtn,
  Input,
  PageHeader,
  Spinner,
  useConfirm,
  useToast,
} from "../../design-system";
import EmployeePicker from "../../Components/Kronaly/Empleados/EmployeePicker";
import {
  addTeamMembers,
  createTeam,
  deleteTeam,
  getEmployees,
  getTeam,
  getTeams,
  removeTeamMember,
  updateTeam,
} from "../../helpers/kronaly_helper";
import useCurrentMembership from "../../helpers/useCurrentMembership";

const unwrap = (r) => {
  const inner = r?.data ?? r;
  return inner?.data ?? inner;
};

const fullName = (e) => `${e?.first_name || ""} ${e?.last_name || ""}`.trim();

const COLOR_PALETTE = [
  "#3dc53a", "#2563eb", "#7c3aed", "#d97706",
  "#0ea5e9", "#ec4899", "#10b981", "#6c757d",
  "#f59e0b", "#dc2626", "#0891b2", "#a855f7",
];

const ABSENCE_TYPE_LABEL = {
  vacation: "Vacaciones",
  personal: "Asunto propio",
  sick: "Baja médica",
  parental: "Parental",
  other: "Ausencia",
};

const STATE_BADGE = {
  working: { tone: "success", label: "Trabajando" },
  paused: { tone: "warning", label: "Pausa" },
  finished: { tone: "info", label: "Finalizada" },
  off: { tone: "neutral", label: "Fuera" },
};

function Equipos() {
  document.title = "Equipos | Kronaly";
  const toast = useToast();
  const confirm = useConfirm();
  const { can } = useCurrentMembership();
  // "Manager view" en Equipos = capability employees:manage (los equipos
  // viven dentro del módulo empleados).
  const isManager = can("employees:manage");

  const [teams, setTeams] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [openTeam, setOpenTeam] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [teamsRes, empRes] = await Promise.all([
        getTeams(),
        getEmployees().catch(() => ({ data: [] })),
      ]);
      setTeams(unwrap(teamsRes) || []);
      setEmployees(unwrap(empRes) || []);
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudieron cargar los equipos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!openTeam) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    getTeam(openTeam.id)
      .then((r) => { if (!cancelled) setDetail(unwrap(r)); })
      .catch(() => { if (!cancelled) setDetail(null); })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [openTeam]);

  const refreshDetail = () => {
    if (!openTeam) return;
    getTeam(openTeam.id).then((r) => setDetail(unwrap(r))).catch(() => {});
  };

  const onDeleteTeam = async (team) => {
    const ok = await confirm({
      title: "Eliminar equipo",
      description: `Eliminar "${team.name}". Los miembros no se borran pero quedan sin equipo.`,
      confirmLabel: "Eliminar",
      cancelLabel: "Cancelar",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await deleteTeam(team.id);
      toast.success("Equipo eliminado");
      setOpenTeam(null);
      load();
    } catch (e) {
      toast.error("No se pudo eliminar", typeof e === "string" ? e : e?.message);
    }
  };

  const onAddMembers = async (employeeIds) => {
    if (!openTeam || employeeIds.length === 0) return;
    try {
      await addTeamMembers(openTeam.id, employeeIds);
      toast.success(`${employeeIds.length} miembro${employeeIds.length === 1 ? "" : "s"} añadido${employeeIds.length === 1 ? "" : "s"}`);
      refreshDetail();
      load();
    } catch (e) {
      toast.error("No se pudo añadir", typeof e === "string" ? e : e?.message);
    }
  };

  const onRemoveMember = async (employeeId) => {
    if (!openTeam) return;
    try {
      await removeTeamMember(openTeam.id, employeeId);
      toast.success("Miembro retirado");
      refreshDetail();
      load();
    } catch (e) {
      toast.error("No se pudo retirar", typeof e === "string" ? e : e?.message);
    }
  };

  return (
    <div className="kr-page">
      <PageHeader
        eyebrow="Estructura organizativa"
        title="Equipos"
        description="Organiza a tu plantilla por equipos con un manager, miembros y métricas en vivo."
        actions={
          isManager ? (
            <Button variant="primary" icon="Plus" onClick={() => { setEditing(null); setShowForm(true); }}>
              Crear equipo
            </Button>
          ) : null
        }
      />

      {error && <Alert tone="danger">{error}</Alert>}

      {loading ? (
        <div style={{ padding: 40, textAlign: "center" }}><Spinner size={20} /></div>
      ) : teams.length === 0 ? (
        <EmptyState
          icon="Users"
          title="Aún no hay equipos"
          description={isManager ? "Crea el primer equipo para empezar a organizar tu plantilla." : "Aún no se ha creado ningún equipo."}
        />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
          {teams.map((t) => (
            <TeamCard key={t.id} team={t} onOpen={() => setOpenTeam(t)} />
          ))}
        </div>
      )}

      <Drawer
        open={!!openTeam}
        onClose={() => setOpenTeam(null)}
        size="xl"
        title={openTeam ? <DrawerTitle team={detail || openTeam} /> : null}
        footer={
          openTeam && isManager ? (
            <>
              <Button variant="dangerSoft" icon="Trash2" onClick={() => onDeleteTeam(detail || openTeam)}>Eliminar</Button>
              <div style={{ flex: 1 }} />
              <Button variant="secondary" icon="Pencil" onClick={() => { setEditing(detail || openTeam); setShowForm(true); }}>
                Editar
              </Button>
            </>
          ) : null
        }
      >
        {detailLoading ? (
          <div style={{ padding: 40, textAlign: "center" }}><Spinner size={20} /></div>
        ) : detail ? (
          <TeamDetail
            detail={detail}
            employees={employees}
            isManager={isManager}
            onAddMembers={onAddMembers}
            onRemoveMember={onRemoveMember}
          />
        ) : null}
      </Drawer>

      <TeamFormDrawer
        open={showForm}
        team={editing}
        employees={employees}
        onClose={() => setShowForm(false)}
        onSaved={() => {
          setShowForm(false);
          setEditing(null);
          load();
          if (openTeam) refreshDetail();
        }}
      />
    </div>
  );
}

function TeamCard({ team, onOpen }) {
  const [hover, setHover] = useState(false);
  return (
    <Card
      padded={false}
      style={{
        overflow: "hidden",
        cursor: "pointer",
        transform: hover ? "translateY(-1px)" : "none",
        boxShadow: hover ? "var(--kr-shadow-sm)" : "var(--kr-shadow-xs)",
        transition: "transform .12s, box-shadow .12s",
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onOpen}
    >
      <div style={{ height: 4, background: team.color }} />
      <div style={{ padding: 18 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "var(--kr-fg-strong)", letterSpacing: "-0.01em" }}>
              {team.name}
            </div>
            <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginTop: 2 }}>
              {team.members_count ?? 0} miembro{(team.members_count ?? 0) === 1 ? "" : "s"}
            </div>
          </div>
        </div>
        {team.manager && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: "var(--kr-neutral-50)", borderRadius: 6, marginBottom: 14 }}>
            <EmployeeAvatar employeeId={team.manager.id} hasAvatar={!!team.manager.avatar_path} name={team.manager.name || "?"} size={24} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, color: "var(--kr-fg-strong)", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {team.manager.name || "(sin nombre)"}
              </div>
              <div style={{ fontSize: 10, color: "var(--kr-fg-muted)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>
                Manager
              </div>
            </div>
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
          <Mini label="Trabajando" value={team.working_count ?? 0} tone="success" />
          <Mini label="Ausentes" value={team.absences_count ?? 0} tone={(team.absences_count ?? 0) > 0 ? "warning" : "neutral"} />
          <Mini label="Pendientes" value={team.pending_count ?? 0} tone={(team.pending_count ?? 0) > 0 ? "info" : "neutral"} />
        </div>
      </div>
    </Card>
  );
}

function Mini({ label, value, tone }) {
  const c = { success: "var(--kr-success-700)", warning: "var(--kr-warning-700)", info: "var(--kr-info-700)", neutral: "var(--kr-fg-strong)" }[tone];
  return (
    <div>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--kr-fg-muted)", fontWeight: 600 }}>{label}</div>
      <div style={{ fontFamily: "var(--kr-font-mono)", fontVariantNumeric: "tabular-nums", fontSize: 22, fontWeight: 700, color: c, marginTop: 2, letterSpacing: "-0.02em" }}>{value}</div>
    </div>
  );
}

function DrawerTitle({ team }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{ width: 8, height: 32, borderRadius: 2, background: team?.color || "#6c757d" }} />
      <div>
        <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600 }}>Equipo</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: "var(--kr-fg-strong)" }}>{team?.name}</div>
      </div>
    </div>
  );
}

function MetricBlock({ label, value, tone }) {
  const c = { success: "var(--kr-success-700)", warning: "var(--kr-warning-700)", info: "var(--kr-info-700)" }[tone] || "var(--kr-fg-strong)";
  return (
    <>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--kr-fg-muted)", fontWeight: 600 }}>{label}</div>
      <div style={{ fontFamily: "var(--kr-font-mono)", fontSize: 28, fontWeight: 700, color: c, marginTop: 4, letterSpacing: "-0.02em" }}>{value}</div>
    </>
  );
}

function formatHours(minutes) {
  if (!minutes && minutes !== 0) return "—";
  const h = Math.floor(minutes / 60);
  const m = String(minutes % 60).padStart(2, "0");
  return `${String(h).padStart(2, "0")}:${m} h`;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    // start_date/end_date son "YYYY-MM-DD" planos: parseamos como fecha LOCAL
    // para no depender de la zona horaria del navegador (new Date("YYYY-MM-DD")
    // se interpreta como medianoche UTC y desfasa el día en offsets negativos).
    const [y, m, d] = String(iso).split("-").map(Number);
    const date = y && m && d ? new Date(y, m - 1, d) : new Date(iso);
    return date.toLocaleDateString("es-ES", { day: "2-digit", month: "short" });
  } catch { return iso; }
}

function TeamDetail({ detail, employees, isManager, onAddMembers, onRemoveMember }) {
  const [addingMember, setAddingMember] = useState(false);
  const [pendingAdd, setPendingAdd] = useState("");

  const memberIds = useMemo(
    () => new Set((detail.members || []).map((m) => m.id)),
    [detail],
  );
  const candidates = useMemo(
    () => (employees || []).filter((e) => !memberIds.has(e.id)),
    [employees, memberIds],
  );

  const onConfirmAdd = () => {
    if (!pendingAdd) return;
    onAddMembers([pendingAdd]);
    setPendingAdd("");
    setAddingMember(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {detail.description && (
        <div style={{ fontSize: 13, color: "var(--kr-fg-strong)", lineHeight: 1.5 }}>
          {detail.description}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        <Card><MetricBlock label="Miembros" value={detail.members_count ?? 0} /></Card>
        <Card><MetricBlock label="Trabajando" value={detail.working_count ?? 0} tone="success" /></Card>
        <Card><MetricBlock label="Ausencias" value={detail.absences_count ?? 0} tone="warning" /></Card>
        <Card><MetricBlock label="Pendientes" value={detail.pending_count ?? 0} tone="info" /></Card>
      </div>

      <Card padded={false}>
        <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--kr-border-subtle)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--kr-fg-strong)" }}>Miembros · estado actual</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Badge tone="success" dot>En tiempo real</Badge>
            {isManager && !addingMember && candidates.length > 0 && (
              <Button size="sm" variant="secondary" icon="Plus" onClick={() => setAddingMember(true)}>
                Añadir
              </Button>
            )}
          </div>
        </div>

        {addingMember && (
          <div style={{ padding: "12px 20px", borderBottom: "1px solid var(--kr-border-subtle)", display: "flex", flexDirection: "column", gap: 10 }}>
            <EmployeePicker
              employees={candidates}
              value={pendingAdd}
              onChange={setPendingAdd}
            />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Button size="sm" variant="ghost" onClick={() => { setAddingMember(false); setPendingAdd(""); }}>Cancelar</Button>
              <Button size="sm" variant="primary" disabled={!pendingAdd} onClick={onConfirmAdd}>Añadir miembro</Button>
            </div>
          </div>
        )}

        {(detail.members || []).length === 0 ? (
          <div style={{ padding: 24, fontSize: 13, color: "var(--kr-fg-muted)", textAlign: "center" }}>
            Equipo sin miembros todavía.
          </div>
        ) : (
          (detail.members || []).map((m, i) => {
            const last = i === detail.members.length - 1;
            const badge = STATE_BADGE[m.state] || STATE_BADGE.off;
            return (
              <div
                key={m.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr 1fr 60px",
                  padding: "10px 20px",
                  alignItems: "center",
                  borderBottom: last ? "none" : "1px solid var(--kr-border-subtle)",
                  fontSize: 13,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <EmployeeAvatar employeeId={m.id} hasAvatar={!!m.avatar_path} name={m.name || "?"} size={28} />
                  <div>
                    <div style={{ color: "var(--kr-fg-strong)", fontWeight: 500 }}>
                      {m.name || "(sin nombre)"}
                      {m.is_manager && (
                        <span style={{ marginLeft: 6, fontSize: 10, color: "var(--kr-fg-muted)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>
                          Manager
                        </span>
                      )}
                    </div>
                    {m.position && <div style={{ color: "var(--kr-fg-muted)", fontSize: 11 }}>{m.position}</div>}
                  </div>
                </div>
                <div>
                  <Badge tone={badge.tone} dot>{badge.label}</Badge>
                </div>
                <div style={{ fontFamily: "var(--kr-font-mono)", color: "var(--kr-fg-strong)" }}>{formatHours(m.minutes_today)}</div>
                <div style={{ textAlign: "right" }}>
                  {isManager && !m.is_manager && (
                    <IconBtn icon="X" aria-label="Retirar miembro" onClick={() => onRemoveMember(m.id)} />
                  )}
                </div>
              </div>
            );
          })
        )}
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--kr-fg-strong)", marginBottom: 12 }}>
            Próximas ausencias
          </div>
          {(detail.upcoming_absences || []).length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>Sin ausencias programadas.</div>
          ) : (
            (detail.upcoming_absences || []).map((a, i, arr) => (
              <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: i < arr.length - 1 ? "1px solid var(--kr-border-subtle)" : "none" }}>
                <EmployeeAvatar employeeId={a.employee_profile_id} hasAvatar={!!a.avatar_path} name={a.employee_name || "?"} size={26} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: "var(--kr-fg-strong)", fontWeight: 500 }}>
                    {a.employee_name || "(sin nombre)"}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>
                    {ABSENCE_TYPE_LABEL[a.type] || a.type} · {formatDate(a.start_date)} – {formatDate(a.end_date)}
                  </div>
                </div>
                <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 12, color: "var(--kr-fg-strong)", fontWeight: 600 }}>{Math.round(a.days_count)}d</span>
              </div>
            ))
          )}
        </Card>

        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--kr-fg-strong)", marginBottom: 12 }}>
            Solicitudes pendientes
          </div>
          {(detail.pending_requests || []).length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>Sin solicitudes pendientes.</div>
          ) : (
            (detail.pending_requests || []).map((a, i, arr) => (
              <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: i < arr.length - 1 ? "1px solid var(--kr-border-subtle)" : "none" }}>
                <EmployeeAvatar employeeId={a.employee_profile_id} hasAvatar={!!a.avatar_path} name={a.employee_name || "?"} size={26} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: "var(--kr-fg-strong)", fontWeight: 500 }}>
                    {a.employee_name || "(sin nombre)"}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>
                    {ABSENCE_TYPE_LABEL[a.type] || a.type}
                  </div>
                </div>
                <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 12, color: "var(--kr-fg-strong)", fontWeight: 600 }}>{Math.round(a.days_count)}d</span>
              </div>
            ))
          )}
        </Card>
      </div>
    </div>
  );
}

function TeamFormDrawer({ open, team, employees, onClose, onSaved }) {
  const toast = useToast();
  const isEdit = !!team;
  const initialForm = () => ({
    name: team?.name || "",
    color: team?.color || COLOR_PALETTE[0],
    description: team?.description || "",
    manager_employee_profile_id: team?.manager_employee_profile_id || team?.manager?.id || "",
    member_ids: [],
    can_approve_absences: team?.can_approve_absences ?? true,
    can_approve_overtime: team?.can_approve_overtime ?? true,
    can_approve_corrections: team?.can_approve_corrections ?? true,
    can_view_documents: team?.can_view_documents ?? true,
    shared_calendar: team?.shared_calendar ?? true,
  });
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) setForm(initialForm());
    else setError(null);
  }, [open, team]);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) {
      setError("El nombre es obligatorio.");
      return;
    }
    setSubmitting(true);
    try {
      const permFields = {
        can_approve_absences: form.can_approve_absences,
        can_approve_overtime: form.can_approve_overtime,
        can_approve_corrections: form.can_approve_corrections,
        can_view_documents: form.can_view_documents,
        shared_calendar: form.shared_calendar,
      };
      if (isEdit) {
        await updateTeam(team.id, {
          name: form.name.trim(),
          color: form.color,
          description: form.description.trim() || null,
          manager_employee_profile_id: form.manager_employee_profile_id || null,
          ...permFields,
        });
        toast.success("Equipo actualizado");
      } else {
        await createTeam({
          name: form.name.trim(),
          color: form.color,
          description: form.description.trim() || undefined,
          manager_employee_profile_id: form.manager_employee_profile_id || undefined,
          member_ids: form.member_ids,
          ...permFields,
        });
        toast.success("Equipo creado");
      }
      onSaved?.();
    } catch (e2) {
      setError(typeof e2 === "string" ? e2 : e2?.message || "No se pudo guardar el equipo.");
    } finally {
      setSubmitting(false);
    }
  };

  const formId = "team-form";

  return (
    <Drawer
      open={open}
      onClose={submitting ? undefined : onClose}
      title={isEdit ? "Editar equipo" : "Crear equipo"}
      size="md"
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose} disabled={submitting}>Cancelar</Button>
          <div style={{ flex: 1 }} />
          <Button variant="primary" type="submit" form={formId} icon={isEdit ? "Save" : "Plus"} disabled={submitting}>
            {submitting ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear equipo"}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {error && <Alert tone="danger">{error}</Alert>}

        <Field label="Nombre" required>
          <Input
            value={form.name}
            onChange={(e) => update({ name: e.target.value })}
            placeholder="Ej. Producto, Ventas, Soporte"
            required
          />
        </Field>

        <Field label="Color" help="Banding visual de las cards.">
          <ColorPalette value={form.color} onChange={(c) => update({ color: c })} />
        </Field>

        <Field label="Descripción">
          <textarea
            value={form.description}
            onChange={(e) => update({ description: e.target.value })}
            rows={3}
            placeholder="Opcional — qué hace este equipo."
            style={{
              width: "100%",
              border: "1px solid var(--kr-border)",
              borderRadius: 6,
              padding: 10,
              fontSize: 13,
              fontFamily: "var(--kr-font-sans)",
              outline: "none",
              resize: "vertical",
              color: "var(--kr-fg-strong)",
            }}
          />
        </Field>

        <Field label="Manager" help="Pasa a ser miembro automáticamente.">
          <EmployeePicker
            employees={employees}
            value={form.manager_employee_profile_id}
            onChange={(id) => update({ manager_employee_profile_id: id })}
            placeholder="Busca al manager…"
          />
        </Field>

        <fieldset style={{ border: "1px solid var(--kr-border-subtle)", borderRadius: 8, padding: "12px 14px", margin: 0 }}>
          <legend style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--kr-fg-muted)", padding: "0 6px" }}>
            Permisos del manager
          </legend>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <PermToggle label="Puede aprobar ausencias" checked={form.can_approve_absences} onChange={(v) => update({ can_approve_absences: v })} />
            <PermToggle label="Puede aprobar horas extra" checked={form.can_approve_overtime} onChange={(v) => update({ can_approve_overtime: v })} />
            <PermToggle label="Puede aprobar correcciones de jornada" checked={form.can_approve_corrections} onChange={(v) => update({ can_approve_corrections: v })} />
            <PermToggle label="Puede ver documentos de empleados" checked={form.can_view_documents} onChange={(v) => update({ can_view_documents: v })} />
          </div>
        </fieldset>

        <fieldset style={{ border: "1px solid var(--kr-border-subtle)", borderRadius: 8, padding: "12px 14px", margin: 0 }}>
          <legend style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--kr-fg-muted)", padding: "0 6px" }}>
            Visibilidad
          </legend>
          <PermToggle label="Calendario compartido entre miembros" checked={form.shared_calendar} onChange={(v) => update({ shared_calendar: v })} />
        </fieldset>

        {!isEdit && (
          <Field label="Miembros iniciales" help="Puedes añadir o quitar miembros desde el detalle del equipo.">
            <MultiEmployeeSelector
              employees={(employees || []).filter((e) => e.id !== form.manager_employee_profile_id)}
              value={form.member_ids}
              onChange={(ids) => update({ member_ids: ids })}
            />
          </Field>
        )}
      </form>
    </Drawer>
  );
}

function PermToggle({ label, checked, onChange }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13 }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: 16, height: 16, accentColor: "var(--kr-action)" }}
      />
      <span>{label}</span>
    </label>
  );
}

function ColorPalette({ value, onChange }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {COLOR_PALETTE.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          style={{
            width: 28,
            height: 28,
            borderRadius: 6,
            background: c,
            border: value === c ? "2px solid var(--kr-fg-strong)" : "1px solid var(--kr-border)",
            cursor: "pointer",
            padding: 0,
          }}
          aria-label={c}
        />
      ))}
    </div>
  );
}

function MultiEmployeeSelector({ employees, value, onChange }) {
  const [picking, setPicking] = useState("");
  const selectedSet = useMemo(() => new Set(value), [value]);
  const candidates = useMemo(
    () => employees.filter((e) => !selectedSet.has(e.id)),
    [employees, selectedSet],
  );
  const selected = useMemo(
    () => value.map((id) => employees.find((e) => e.id === id)).filter(Boolean),
    [value, employees],
  );

  const onAdd = () => {
    if (!picking) return;
    onChange([...value, picking]);
    setPicking("");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {selected.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {selected.map((emp) => (
            <span
              key={emp.id}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 8px",
                fontSize: 12,
                background: "var(--kr-action-soft-bg)",
                color: "var(--kr-action-soft-fg)",
                borderRadius: 999,
              }}
            >
              <EmployeeAvatar employeeId={emp.id} hasAvatar={!!emp.avatar_path} name={fullName(emp) || "?"} size={18} />
              {fullName(emp) || "(sin nombre)"}
              <button
                type="button"
                onClick={() => onChange(value.filter((id) => id !== emp.id))}
                style={{ border: "none", background: "transparent", cursor: "pointer", color: "inherit", padding: 0, fontSize: 14, lineHeight: 1 }}
                aria-label="Quitar"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <EmployeePicker
          employees={candidates}
          value={picking}
          onChange={setPicking}
          placeholder="Añade un miembro…"
        />
        {picking && (
          <Button size="sm" variant="secondary" onClick={onAdd} icon="Plus">
            Añadir al equipo
          </Button>
        )}
      </div>
    </div>
  );
}

export default Equipos;
